import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ThemeProvider,
  CssBaseline,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Stack,
  Tabs,
  Tab,
  Badge,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import { theme } from "../../theme";
import { useOutlookPlugin } from "../../hooks/useOutlookPlugin";
import { ResponsiveTimelineWrapper } from "./ResponsiveTimelineWrapper";
import { ShipmentActionsComponent } from "./ShipmentActionsComponent";
import { AllActionsComponent } from "./AllActionsComponent";
import type {
  FindShipmentByMessageIdResponse,
  ShipmentFeedOverview,
} from "@services/shipments/types";
import type {
  ReminderWithAction,
  GetMyRemindersResponse,
} from "@services/emailAutomationPipeline/types";
import { ShipmentSelectionList } from "./ShipmentSelectionList";
import { ShipmentSearchPanel } from "./ShipmentSearchPanel";
import { EmailPreview } from "./EmailPreview";
import { useSessionRecording } from "@/components/useSessionRecording";
import { EventLogger } from "@shared/utils/eventLogger";

interface ShipmentTimelineAndAutomationPageProps {
  onBack: () => void;
}

export const ShipmentTimelineAndAutomationPage: React.FC<
  ShipmentTimelineAndAutomationPageProps
> = ({ onBack }) => {
  const {
    currentMessageId,
    currentSharedInbox,
    isReady,
    apiClient,
    overrideShipmentId,
    useFeatureFlag,
  } = useOutlookPlugin();
  const showActions = useFeatureFlag("showActions") != null;
  const [shipments, setShipments] = useState<ShipmentFeedOverview[]>([]);
  const [selectedShipmentFeedId, setSelectedShipmentFeedId] = useState<
    string | null
  >(null);
  const [autoSelected, setAutoSelected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewEmailId, setPreviewEmailId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [allReminders, setAllReminders] = useState<ReminderWithAction[]>([]);
  const [allReminderTypes, setAllReminderTypes] = useState<
    { type: string; name: string; count: number }[]
  >([]);
  const [allRemindersLoading, setAllRemindersLoading] = useState(true);
  const allRemindersFilterRef = useRef<{ types: string[]; showResolved: boolean }>({
    types: [],
    showResolved: false,
  });
  const [overrideFromAction, setOverrideFromAction] = useState<string | null>(
    null,
  );
  const [searchOpen, setSearchOpen] = useState(false);
  const overrideSource = useRef<"search" | "actions" | null>(null);

  const fetchMyActions = useCallback(
    async (params?: { types?: string[]; showResolved?: boolean }, silent = false) => {
      try {
        if (!silent) setAllRemindersLoading(true);
        const qs = new URLSearchParams();
        const types = params?.types ?? allRemindersFilterRef.current.types;
        const showResolved = params?.showResolved ?? allRemindersFilterRef.current.showResolved;
        if (types.length > 0) qs.set("types", types.join(","));
        if (showResolved) qs.set("showResolved", "true");
        const qsStr = qs.toString();

        const response = await apiClient.get<GetMyRemindersResponse>(
          `/email-automation/actions/my-actions${qsStr ? `?${qsStr}` : ""}`,
        );
        setAllReminders(response.reminders);
        setAllReminderTypes(response.reminderTypes);
      } catch (err) {
        console.error("Error fetching my actions:", err);
      } finally {
        if (!silent) setAllRemindersLoading(false);
      }
    },
    [apiClient],
  );

  // Pre-load all actions on mount + 60s polling
  useEffect(() => {
    if (!showActions) return;
    fetchMyActions();
    const interval = setInterval(() => fetchMyActions(undefined, true), 60_000);
    return () => clearInterval(interval);
  }, [showActions, fetchMyActions]);

  useSessionRecording("6jyi7b/shipmenttimelineandautomationoutlookplugin");

  const findShipment = useCallback(
    async (messageId: string, sharedInbox?: string | null) => {
      setLoading(true);
      setError(null);
      try {
        const body: { messageId: string; sharedInbox?: string } = { messageId };
        if (sharedInbox) {
          body.sharedInbox = sharedInbox;
        }
        const response = await apiClient.post<FindShipmentByMessageIdResponse>(
          "/shipments/find-by-message-id",
          body,
        );
        setShipments(response.shipments);
        if (response.shipments.length === 1) {
          setSelectedShipmentFeedId(response.shipments[0].shipmentFeedId);
          setAutoSelected(true);
        }
      } catch (err) {
        console.error("Error finding shipment:", err);
        setError("No shipment found for this email");
        setShipments([]);
      } finally {
        setLoading(false);
      }
    },
    [apiClient],
  );

  const pageOpenTracked = useRef(false);

  useEffect(() => {
    setShipments([]);
    setSelectedShipmentFeedId(null);
    setAutoSelected(false);
    setLoading(false);
    setError(null);
    pageOpenTracked.current = false;

    if (overrideShipmentId) {
      setSelectedShipmentFeedId(overrideShipmentId);
      return;
    }

    if (!isReady || !currentMessageId) {
      return;
    }
    findShipment(currentMessageId, currentSharedInbox);
  }, [
    currentMessageId,
    currentSharedInbox,
    isReady,
    findShipment,
    overrideShipmentId,
  ]);

  // Track page-open event after findShipment resolves (or when there's no messageId)
  useEffect(() => {
    if (pageOpenTracked.current) return;
    if (loading) return;
    if (overrideShipmentId) return;

    // Either no messageId, or findShipment has resolved (loading finished)
    if (currentMessageId) {
      pageOpenTracked.current = true;
      EventLogger.getInstance().track({
        name: "outlook_plugin_open_product_shipment_timeline",
        messageId: currentMessageId ?? null,
        shipmentFound: shipments.length > 0,
        shipmentCount: shipments.length,
        error: error ?? null,
      });
    }
  }, [loading, currentMessageId, shipments, error, overrideShipmentId]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setOverrideFromAction(null);
  };

  const handleGoToShipment = (shipmentId: string) => {
    overrideSource.current = "actions";
    setOverrideFromAction(shipmentId);
    setSelectedShipmentFeedId(shipmentId);
    setActiveTab(0);
  };

  const handleBackFromOverride = () => {
    const goToActions = overrideSource.current === "actions";
    overrideSource.current = null;
    setOverrideFromAction(null);
    setSelectedShipmentFeedId(null);
    if (goToActions) {
      setActiveTab(1);
    }
  };

  const showTimeline =
    (selectedShipmentFeedId || overrideFromAction) && !loading;
  const showSelectionList =
    !overrideFromAction &&
    !loading &&
    !error &&
    shipments.length > 1 &&
    !selectedShipmentFeedId;
  const showNoShipment =
    !overrideFromAction &&
    !loading &&
    !error &&
    !overrideShipmentId &&
    currentMessageId &&
    shipments.length === 0;
  const currentShipmentId = overrideFromAction || selectedShipmentFeedId;

  useEffect(() => {
    if (currentShipmentId) {
      EventLogger.getInstance().track({
        name: "shipment_timeline_open",
        shipmentId: currentShipmentId,
      });
    }
  }, [currentShipmentId]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header: Tabs (when actions enabled) or simple bar */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
          {overrideFromAction ? (
            <Box
              sx={{
                bgcolor: "black",
                minHeight: "48px",
                display: "flex",
                alignItems: "center",
                px: 1,
              }}
            >
              <IconButton
                size="small"
                onClick={handleBackFromOverride}
                sx={{ color: "#FEDF33" }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: "#FEDF33",
                  fontWeight: 600,
                  ml: 0.5,
                }}
              >
                Shipment
              </Typography>
            </Box>
          ) : showActions ? (
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                bgcolor: "black",
                minHeight: "48px",
                "& .MuiTab-root": {
                  color: "rgba(255, 255, 255, 0.7)",
                  minHeight: "48px",
                },
                "& .Mui-selected": {
                  color: "#FEDF33 !important",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#FEDF33",
                },
              }}
            >
              <Tab label="Shipment" />
              <Tab
                label={
                  <Badge
                    badgeContent={allRemindersFilterRef.current.showResolved ? 0 : allReminders.length}
                    color="error"
                    sx={{
                      "& .MuiBadge-badge": {
                        backgroundColor: "#FEDF33",
                        color: "black",
                        fontWeight: "bold",
                        right: "-8px",
                      },
                    }}
                  >
                    My Actions
                  </Badge>
                }
              />
            </Tabs>
          ) : (
            <Box
              sx={{
                bgcolor: "black",
                minHeight: "48px",
                display: "flex",
                alignItems: "center",
                px: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#FEDF33",
                  fontWeight: 600,
                }}
              >
                Shipment
              </Typography>
            </Box>
          )}
        </Box>

        {/* Tab Content */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Tab 0: Current Shipment (also shown when navigating from My Actions) */}
          {activeTab === 0 && (
            <>
              {/* Search trigger */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={0.5}
                sx={{ px: 1, pt: 1, cursor: "pointer" }}
                onClick={() => setSearchOpen(true)}
              >
                <Typography variant="body2" color="text.secondary">
                  Search Shipment
                </Typography>
                <IconButton size="small">
                  <SearchIcon fontSize="small" />
                </IconButton>
              </Stack>

              {/* No message selected */}
              {!currentMessageId &&
                !overrideShipmentId &&
                !overrideFromAction &&
                !loading && (
                  <Box sx={{ p: 2 }}>
                    <Alert severity="info">
                      Select an email to view the shipment timeline.
                    </Alert>
                  </Box>
                )}

              {/* Loading */}
              {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              )}

              {/* Error */}
              {error && !overrideFromAction && (
                <Box sx={{ p: 2 }}>
                  <Alert severity="info">{error}</Alert>
                </Box>
              )}

              {/* No shipment found */}
              {showNoShipment && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No shipment found for this email.
                  </Typography>
                </Box>
              )}

              {/* Multiple shipments - selection list */}
              {showSelectionList && (
                <ShipmentSelectionList
                  shipments={shipments}
                  onSelect={(id) => {
                    setSelectedShipmentFeedId(id);
                    setAutoSelected(false);
                  }}
                />
              )}

              {/* Shipment timeline */}
              {showTimeline && currentShipmentId && (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {/* Back button only when multiple shipments and manually selected */}
                  {!overrideFromAction &&
                    shipments.length > 1 &&
                    !autoSelected && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ px: 1, pt: 1 }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => setSelectedShipmentFeedId(null)}
                        >
                          <ArrowBackIcon fontSize="small" />
                        </IconButton>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ cursor: "pointer" }}
                          onClick={() => setSelectedShipmentFeedId(null)}
                        >
                          Back to shipments ({shipments.length})
                        </Typography>
                      </Stack>
                    )}
                  {showActions && (
                    <ShipmentActionsComponent shipmentId={currentShipmentId} />
                  )}
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <ResponsiveTimelineWrapper
                      shipmentId={currentShipmentId}
                      isAdmin={false}
                      filtersCollapsible
                      inlineDates
                      onOpenEmail={(emailId) => setPreviewEmailId(emailId)}
                    />
                  </Box>
                </Box>
              )}
            </>
          )}

          {/* Tab 1: My Actions (only when actions enabled) */}
          {showActions && activeTab === 1 && (
            <AllActionsComponent
              onGoToShipment={handleGoToShipment}
              reminders={allReminders}
              setReminders={setAllReminders}
              reminderTypes={allReminderTypes}
              loading={allRemindersLoading}
              onRefetch={(params) => {
                if (params) {
                  allRemindersFilterRef.current = {
                    types: params.types ?? allRemindersFilterRef.current.types,
                    showResolved: params.showResolved ?? allRemindersFilterRef.current.showResolved,
                  };
                }
                fetchMyActions(params);
              }}
            />
          )}
        </Box>

        {previewEmailId && (
          <EmailPreview
            mode="readonly"
            emailId={previewEmailId}
            onDismiss={() => setPreviewEmailId(null)}
          />
        )}

        <ShipmentSearchPanel
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onSelectShipment={(shipmentId) => {
            setSearchOpen(false);
            overrideSource.current = "search";
            setOverrideFromAction(shipmentId);
            setSelectedShipmentFeedId(shipmentId);
            setActiveTab(0);
          }}
        />
      </Box>
    </ThemeProvider>
  );
};
